/**
 * <ReadToggleButton> — Bouton compact pour marquer manuellement comme lu / non lu.
 *
 * Batch #14.93 — Affiché à côté de chaque ligne dans Suivi comms, Envois,
 * Dépôts, Factures, Chat, Documents à signer.
 *
 * Props :
 *   - isReallyUnread : true si le client n'a pas lu (read_at null)
 *   - isOverridden   : true si admin a déjà marqué cette ligne comme lue
 *   - onMarkRead     : callback () => Promise
 *   - onMarkUnread   : callback () => Promise
 *   - size           : "sm" | "md" (défaut "sm")
 */
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";

export default function ReadToggleButton({
  isReallyUnread,
  isOverridden,
  onMarkRead,
  onMarkUnread,
  size = "sm",
  testId,
}) {
  const [busy, setBusy] = useState(false);

  // Si le client a lu pour de vrai, pas besoin de toggle admin (toujours lu)
  if (!isReallyUnread && !isOverridden) return null;

  const isMarkedRead = isOverridden;
  const Icon = busy ? Loader2 : (isMarkedRead ? EyeOff : Eye);

  const handleClick = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      if (isMarkedRead) await onMarkUnread();
      else await onMarkRead();
    } finally {
      setBusy(false);
    }
  };

  const sz = size === "md" ? "w-7 h-7" : "w-5 h-5";
  const iconSz = size === "md" ? "w-4 h-4" : "w-3 h-3";

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className={`${sz} rounded-full flex items-center justify-center transition ${
        isMarkedRead
          ? "text-emerald-600 hover:bg-emerald-50"
          : "text-amber-600 hover:bg-amber-50"
      } disabled:opacity-50`}
      title={isMarkedRead ? "Marqué comme lu — cliquer pour annuler" : "Marquer comme lu"}
      data-testid={testId || "read-toggle-btn"}
    >
      <Icon className={`${iconSz} ${busy ? "animate-spin" : ""}`} />
    </button>
  );
}
